import { Check, Delete } from '@mui/icons-material';
import React, { InputHTMLAttributes } from 'react';
import styled from 'styled-components';
import { CommonText } from 'ui/theme/Color';

// --- 1. 타입 정의 ---

// 기본 input 속성(checked, onChange, disabled 등)을 모두 상속받고,
// 커스텀으로 필요한 속성(label)만 추가합니다.
interface CustomRadioProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  deleteDisabled?: boolean;
  onDelete?: () => void;
}

// styled-components에 전달할 Props 타입
// DOM 요소로 전달되지 않도록 '$' 접두사를 사용하는 Transient Props를 적용합니다.
interface StyledLabelProps {
  $isChecked?: boolean;
  $isDisabled?: boolean;
}

// --- 2. Styled Components 정의 ---
const StyledLabel = styled.label<StyledLabelProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  margin: 0 5px;
  border-radius: 9999px;
  font-family: sans-serif;
  font-size: 14px;
  font-weight: 500;
  user-select: none;
  transition: all 0.2s ease-in-out;
  background-color: #ffffff;

  /* disabled 상태에 따른 커서 및 투명도 변경 */
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.6 : 1)};

  /* checked 상태에 따른 동적 스타일링 */
  border: 1.5px solid ${({ $isChecked }) => ($isChecked ? CommonText.Primary : CommonText.Lighter)};
  color: ${({ $isChecked, $isDisabled }) =>
    $isDisabled ? CommonText.Light : $isChecked ? CommonText.Primary : CommonText.Lighter};
`;

const HiddenInput = styled.input.attrs({ type: 'radio' })`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

// --- 3. 커스텀 체크박스 컴포넌트 ---

const CustomRadio: React.FC<CustomRadioProps> = ({
  id,
  label,
  checked,
  disabled,
  deleteDisabled,
  onChange,
  onDelete,
  ...props // 나머지 모든 input 속성들을 모읍니다.
}) => {
  const onClick = (e) => {
    e.stopPropagation();
    if (onDelete) onDelete();
  };
  return (
    <StyledLabel htmlFor={id} $isChecked={checked} $isDisabled={disabled}>
      <HiddenInput
        id={id}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        {...props} // id, name, required, onChange 등을 기본 input에 그대로 전달합니다.
      />
      <span>{label}</span>

      {checked && (
        <>
          <Check sx={{ marginLeft: 1, fontSize: 16, color: CommonText.Primary }} />
          {!disabled && !deleteDisabled && (
            <button onClick={onClick}>
              <Delete
                sx={{
                  marginLeft: 1,
                  fontSize: 16,
                  color: CommonText.Primary,
                  '&:hover': {
                    color: CommonText.Error,
                  },
                }}
              />
            </button>
          )}
        </>
      )}
    </StyledLabel>
  );
};

export default CustomRadio;
